import React, {FC, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {useEventHandler} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {isLoadedMessage} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {ResolveStateProps} from '../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {isMessageActivity} from '../../../../models/api/activityModel';
import {ConversationAiMarkModel} from '../../../../models/api/conversationAiMarkModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {fetchActivityAsyncAction} from '../../../../store/data/activities/activitiesActions';
import {makeGetVisualRecipientMap} from '../../../../store/data/contacts/recipientsSelectors';
import {getActivities, getMessages} from '../../../../store/data/dataSelectors';
import {AiTaggingInaccurateDialog, AiTaggingInaccurateDialogProps} from './aiTaggingInaccurateDialog';

/*
 * Props.
 */

interface AiTaggingInaccurateDialogContainerProps {
  aiMark: ConversationAiMarkModel;
  conversation: ConversationModel;
}

type AiTaggingInaccurateDialogContainerComponentProps = AiTaggingInaccurateDialogContainerProps &
  ResolveStateProps<typeof mapStateToProps> &
  ResolveDispatchProps<typeof mapDispatchToProps> &
  Pick<AiTaggingInaccurateDialogProps, 'panelId' | 'conversation' | 'tag' | 'tagsPath' | 'onRequestClose'>;

/*
 * Selectors.
 */

function getActivityId(_state: unknown, props: AiTaggingInaccurateDialogContainerProps) {
  return props.aiMark.activityId;
}

const getMessage = createSelector(
  [getActivityId, getActivities, getMessages],
  (activityId, activities, messages) => {
    if (!activityId) {
      return undefined;
    }

    const activity = activities[activityId];
    if (!activity || !isMessageActivity(activity)) {
      return undefined;
    }

    const message = messages[activity.messageUid];
    if (!message || !isLoadedMessage(message)) {
      return undefined;
    }
    return message;
  },
);

function getConversationNamespace(_state: unknown, props: AiTaggingInaccurateDialogContainerProps) {
  return props.conversation.namespace;
}

/*
 * Component.
 */

const AiTaggingInaccurateDialogContainerComponent: FC<AiTaggingInaccurateDialogContainerComponentProps> = (
  props,
) => {
  const {
    panelId,
    message,
    aiMark,
    conversation,
    tag,
    tagsPath,
    recipients,
    onRequestClose,
    fetchActivityAsync,
  } = props;
  const [asyncStatus, setAsyncStatus] = useState(
    message ? AsyncStatusesEnum.LOADED : AsyncStatusesEnum.LOADING,
  );

  const onMessageRequest = useEventHandler(async () => {
    if (message) {
      setAsyncStatus(AsyncStatusesEnum.LOADED);
      return;
    }

    setAsyncStatus(AsyncStatusesEnum.LOADING);
    try {
      await fetchActivityAsync(conversation.id, aiMark.activityId);
      setAsyncStatus(AsyncStatusesEnum.LOADED);
    } catch (error) {
      setAsyncStatus(AsyncStatusesEnum.FAILED);
    }
  });

  useEffect(() => {
    onMessageRequest();
  }, [aiMark.activityId, onMessageRequest]);

  return (
    <AiTaggingInaccurateDialog
      panelId={panelId}
      aiMark={aiMark}
      tag={tag}
      tagsPath={tagsPath}
      asyncStatus={asyncStatus}
      message={message}
      recipients={recipients}
      conversation={conversation}
      onRequestClose={onRequestClose}
      onRetryClick={onMessageRequest}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AiTaggingInaccurateDialogContainerProps>()(() => ({
  message: getMessage,
  recipients: makeGetVisualRecipientMap(getConversationNamespace, getMessage),
}));

const mapDispatchToProps = {
  fetchActivityAsync: fetchActivityAsyncAction,
};

export const AiTaggingInaccurateDialogContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AiTaggingInaccurateDialogContainerComponent);
